/**
 * MEMOLINK data model (schema v2).
 *
 * Records are stored flat with a parent id and a position, instead of nested arrays,
 * so each record can later be synced to a cloud table on its own.
 * IDs are created once and never change: Workspace > Category > Action.
 */
export type Id = string;
export type IsoDate = string;

export type ActionType = "link" | "memo";
export type OpenWith = "default" | "chrome" | "safari" | "app";

interface Base {
  id: Id;
  name: string;
  icon: string;
  position: number;
  /** true only for records created by the sample data set */
  sample?: boolean;
  createdAt: IsoDate;
  updatedAt: IsoDate;
}

export interface Workspace extends Base {}

/**
 * Category type: how the Actions inside are laid out. Chosen by the person, separate from the icon and from the
 * icon picker's groups. Records saved before layouts existed have none and read as "standard" (the original grid).
 */
export type CategoryLayout = "work" | "links" | "places" | "lifestyle" | "media" | "shop" | "inspiration" | "standard";
export const DEFAULT_LAYOUT: CategoryLayout = "standard";
export const CATEGORY_LAYOUTS: readonly { key: CategoryLayout; name: string; hint: string }[] = [
  { key: "work", name: "งานและเอกสาร", hint: "รายการแนวตั้ง" },
  { key: "links", name: "ลิงก์และสื่อสาร", hint: "ปุ่มทางลัด" },
  { key: "places", name: "สถานที่และผู้คน", hint: "การ์ดขนาดกลาง" },
  { key: "lifestyle", name: "ไลฟ์สไตล์", hint: "การ์ดสี่เหลี่ยม" },
  { key: "media", name: "ความบันเทิง", hint: "การ์ดภาพปก" },
  { key: "shop", name: "ช้อปปิ้ง", hint: "การ์ดสินค้า 2 คอลัมน์" },
  { key: "inspiration", name: "Inspiration", hint: "Moodboard" },
  { key: "standard", name: "อื่น ๆ — มาตรฐาน", hint: "กริดไอคอนเดิม" }
];

export interface Category extends Base {
  workspaceId: Id;
  /** colour key of the glow under the key frame */
  glow: string;
  layout: CategoryLayout;
}

export interface Action extends Base {
  categoryId: Id;
  type: ActionType;
  /** icon colour, chosen separately from the category glow */
  color: string;
  url: string;
  text: string;
}

export interface Settings {
  activeWorkspaceId: Id | null;
  openWith: OpenWith;
}

export interface Library {
  workspaces: Workspace[];
  categories: Category[];
  actions: Action[];
  settings: Settings;
}

export type EntityKind = "workspace" | "category" | "action";

export const SCHEMA_VERSION = 2;
export const OPEN_WITH: readonly OpenWith[] = ["default", "chrome", "safari", "app"];

export const emptyLibrary = (): Library => ({
  workspaces: [], categories: [], actions: [],
  settings: { activeWorkspaceId: null, openWith: "default" }
});
